import { Schema } from "effect";
import {
  ChannelThreadId,
  DraftMessageId,
  MessageId,
  TopicId,
} from "../../../ids/definitions";
import { MongoUserId } from "../../../ids/mongo-ids";
import { MessageContentType } from "./add-message-input";
import { MessageChannel, MessageSenderType } from "./message";
import { Attachment } from "./message/attachment";
import { EmailRecipientSchema } from "./support-ticket";

// ===== Channel types =====

export const EmailDraftChannel = Schema.Literal(
  "gmail",
  "outlook",
  "customEmail",
);
export type EmailDraftChannel = typeof EmailDraftChannel.Type;

export const NonEmailDraftChannel = Schema.Literal(
  "facebookDm",
  "facebookComments",
  "instagramDm",
  "instagramComments",
  "redoChat",
  "sms",
  "attentive",
  "postscript",
  "tiktok",
  "voice",
  "contactForm",
  "internalNote",
  "mobileMessage",
);
export type NonEmailDraftChannel = typeof NonEmailDraftChannel.Type;

// ===== Channel data =====

export const EmailDraftChannelData = Schema.Struct({
  to: Schema.optional(Schema.Array(EmailRecipientSchema)),
  cc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  bcc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  replyType: Schema.optional(Schema.Literal("reply", "replyAll", "forward")),
});
export type EmailDraftChannelData = typeof EmailDraftChannelData.Type;

// ===== SaveDraftInput =====

const SaveDraftInputBase = {
  threadId: ChannelThreadId,
  inReplyToMessageId: Schema.optional(MessageId),
  contentType: Schema.optional(MessageContentType),
  content: Schema.String,
  plainContent: Schema.optional(Schema.String),
  attachments: Schema.Array(Attachment),
  /**
   * Best-effort hint: the id of the draft this save is updating, if the client
   * already has one. When present and it resolves to a draft in scope, the
   * server reuses that draft's persisted `topicId` instead of re-running the
   * (contention-prone) thread→origin-topic resolution on every debounced
   * autosave. Ignored when absent or stale (e.g. the first save of a new
   * draft), in which case the server resolves the topic as usual.
   */
  existingDraftId: Schema.optional(DraftMessageId),
  /**
   * Mongo user ids referenced by `@`-mentions inside the draft content.
   * Persisted on the draft so they survive draft saves/reloads. Only used
   * when `channel` is `internalNote`; other channels ignore this field.
   */
  mentionedMongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
};

const EmailSaveDraftInput = Schema.Struct({
  ...SaveDraftInputBase,
  channel: EmailDraftChannel,
  channelData: EmailDraftChannelData,
});

const NonEmailSaveDraftInput = Schema.Struct({
  ...SaveDraftInputBase,
  channel: NonEmailDraftChannel,
  channelData: Schema.Null,
});

export const SaveDraftInput = Schema.Union(
  EmailSaveDraftInput,
  NonEmailSaveDraftInput,
);
export type SaveDraftInput = typeof SaveDraftInput.Type;

// ===== DraftResponse =====

export const DraftMessageStatus = Schema.Literal(
  "PENDING",
  "SENDING",
  "ERROR",
  "DELETED",
);
export type DraftMessageStatus = typeof DraftMessageStatus.Type;

export const DraftMessageKind = Schema.Literal(
  "merchant",
  "ai_copilot",
  "autopilot_send",
);
export type DraftMessageKind = typeof DraftMessageKind.Type;

export const DraftMessageCandidateState = Schema.Literal(
  "visible",
  "accepted",
  "declined",
  "superseded",
);
export type DraftMessageCandidateState = typeof DraftMessageCandidateState.Type;

const DraftResponseBase = {
  id: DraftMessageId,
  /**
   * Null for `autopilot_send` drafts. The FE never sees those — repo queries
   * that surface drafts to merchants filter to `kind IN ('merchant',
   * 'ai_copilot')`. Null here only flows through internal write paths
   * (`DraftService.upsertDraftCore`'s return is consumed by the AI agent
   * which discards it / uses only `draft.id`).
   */
  topicId: Schema.NullOr(TopicId),
  threadId: ChannelThreadId,
  inReplyToMessageId: Schema.OptionFromUndefinedOr(MessageId),
  senderMongoUserId: Schema.NullOr(MongoUserId),
  senderType: MessageSenderType,
  contentType: MessageContentType,
  content: Schema.String,
  plainContent: Schema.NullOr(Schema.String),
  attachments: Schema.Array(Attachment),
  /**
   * Mongo user ids of teammates referenced by `@`-mentions in the draft
   * content. Empty/absent for non-`internalNote` drafts.
   */
  mentionedMongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
  status: DraftMessageStatus,
  version: Schema.Number,
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
};

const EmailDraftResponseFields = {
  channel: EmailDraftChannel,
  channelData: EmailDraftChannelData,
};

const NonEmailDraftResponseFields = {
  channel: NonEmailDraftChannel,
  channelData: Schema.Null,
};

const MerchantDraftResponseBase = {
  ...DraftResponseBase,
  kind: Schema.Literal("merchant"),
  candidateState: Schema.Null,
};

const AiCopilotDraftResponseBase = {
  ...DraftResponseBase,
  kind: Schema.Literal("ai_copilot"),
  candidateState: DraftMessageCandidateState,
};

const AutopilotSendDraftResponseBase = {
  ...DraftResponseBase,
  kind: Schema.Literal("autopilot_send"),
  candidateState: Schema.Null,
};

const EmailMerchantDraftResponse = Schema.Struct({
  ...MerchantDraftResponseBase,
  ...EmailDraftResponseFields,
});
const NonEmailMerchantDraftResponse = Schema.Struct({
  ...MerchantDraftResponseBase,
  ...NonEmailDraftResponseFields,
});
export const MerchantDraftResponse = Schema.Union(
  EmailMerchantDraftResponse,
  NonEmailMerchantDraftResponse,
);
export type MerchantDraftResponse = typeof MerchantDraftResponse.Type;

const EmailAiCopilotDraftResponse = Schema.Struct({
  ...AiCopilotDraftResponseBase,
  ...EmailDraftResponseFields,
});
const NonEmailAiCopilotDraftResponse = Schema.Struct({
  ...AiCopilotDraftResponseBase,
  ...NonEmailDraftResponseFields,
});
export const AiCopilotDraftResponse = Schema.Union(
  EmailAiCopilotDraftResponse,
  NonEmailAiCopilotDraftResponse,
);
export type AiCopilotDraftResponse = typeof AiCopilotDraftResponse.Type;

const EmailAutopilotSendDraftResponse = Schema.Struct({
  ...AutopilotSendDraftResponseBase,
  ...EmailDraftResponseFields,
});
const NonEmailAutopilotSendDraftResponse = Schema.Struct({
  ...AutopilotSendDraftResponseBase,
  ...NonEmailDraftResponseFields,
});
export const AutopilotSendDraftResponse = Schema.Union(
  EmailAutopilotSendDraftResponse,
  NonEmailAutopilotSendDraftResponse,
);
export type AutopilotSendDraftResponse = typeof AutopilotSendDraftResponse.Type;

export const DraftResponse = Schema.Union(
  MerchantDraftResponse,
  AiCopilotDraftResponse,
  AutopilotSendDraftResponse,
);
export type DraftResponse = typeof DraftResponse.Type;

export const ThreadDraftsResponse = Schema.Struct({
  drafts: Schema.Array(DraftResponse),
});
export type ThreadDraftsResponse = typeof ThreadDraftsResponse.Type;

// ===== SendMessagePayload (RPC) =====

/**
 * RPC payload for sending a message. All sends require a draft.
 * `actor` and support ticket context are set server-side from auth context and URL path.
 *
 * `mentionedMongoUserIds` is NOT carried here — it is persisted on the draft
 * itself by the compose UI's save path, and the sending service reads it from
 * the draft row when producing the internal-note message.
 */
export const SendMessagePayload = Schema.Struct({
  channel: MessageChannel,
  threadId: ChannelThreadId,
  draftId: DraftMessageId,
});
export type SendMessagePayload = typeof SendMessagePayload.Type;

// ===== SaveDraftAndSend (RPC) =====

/**
 * Response for the merchant `saveDraftAndSend` RPC. The server persists the
 * composer's content as a draft and starts the outbound Temporal workflow in
 * one call, returning everything the client needs to drive the undo window and
 * issue a cancel:
 * - `draftId`: the persisted draft (the optimistic composer draft reconciles to
 *   this id).
 * - `workflowId`: pass to `cancelSend` to undo before dispatch.
 * - `sendAt`: ISO timestamp the send dispatches (end of the undo window); the
 *   client drives its countdown from this rather than the click time, so a slow
 *   save doesn't shrink the user's undo window.
 *
 * The request payload is a plain {@link SaveDraftInput} — the exact content in
 * the composer at send time.
 */
export const SaveDraftAndSendResponse = Schema.Struct({
  draftId: DraftMessageId,
  workflowId: Schema.String,
  sendAt: Schema.String,
});
export type SaveDraftAndSendResponse = typeof SaveDraftAndSendResponse.Type;

export class NotImplementedError extends Schema.TaggedError<NotImplementedError>()(
  "NotImplementedError",
  { message: Schema.String },
) {}
